/**
 * @fileoverview Login data source, type definitions, and login logging utilities.
 * Displays immutable login records and provides functions to log authentication attempts.
 */

import { LoginSchema, type Login } from '@schema/index';
import { createBaseDataSource } from './base-data-source';
import { createBooleanField, createCreatedAtField, createIdField, createJsonField, createStringField, FIELD_WIDTHS } from './field-factories';
import { getCache } from './cache-registry';
import { apiClient } from './api-client';

export type { Login };

export const loginDataSource = createBaseDataSource<Login>({
  endpoint: 'login',
  searchField: 'account_id',
  schema: LoginSchema,
  fields: [
    createIdField(),
    createStringField('account_id', 'Account ID', FIELD_WIDTHS.SMALL),
    createStringField('ip_address', 'IP Address', FIELD_WIDTHS.SMALL),
    createStringField('user_agent', 'User Agent', FIELD_WIDTHS.XLARGE),
    createStringField('device', 'Device', FIELD_WIDTHS.MEDIUM),
    createBooleanField('success', 'Success', FIELD_WIDTHS.SMALL),
    createStringField('failure_reason', 'Failure Reason', FIELD_WIDTHS.MEDIUM),
    createJsonField('metadata', 'Metadata', FIELD_WIDTHS.MEDIUM),
    createCreatedAtField(),
  ],
});

export const loginCache = getCache('login');

// ============================================================================
// Login Logging
// ============================================================================

/**
 * Client-side metadata collected during login attempts.
 */
export interface LoginClientMetadata {
  // Screen information
  screen_width?: number;
  screen_height?: number;
  screen_color_depth?: number;
  screen_pixel_ratio?: number;
  viewport_width?: number;
  viewport_height?: number;

  // Browser/environment info
  timezone?: string;
  timezone_offset?: number;
  language?: string;
  languages?: string[];
  platform?: string;
  vendor?: string;
  cookie_enabled?: boolean;
  do_not_track?: string | null;
  online?: boolean;
  hardware_concurrency?: number;
  device_memory?: number;
  max_touch_points?: number;

  // Connection info (if available)
  connection_type?: string;
  connection_effective_type?: string;
  connection_downlink?: number;
  connection_rtt?: number;
  connection_save_data?: boolean;

  // Page context
  referrer?: string;
  page_url?: string;
  page_title?: string;

  // Timestamp info
  client_timestamp?: string;
  client_timezone_name?: string;

  // Auth provider details
  auth_provider?: string;
  auth_method?: string;
}

/**
 * Request body for login log endpoint.
 */
export interface LogLoginRequest {
  success: boolean;
  failure_reason?: string;
  user_agent?: string;
  metadata?: LoginClientMetadata;
}

/**
 * Collects comprehensive client-side metadata for login logging.
 * Safely handles browsers that don't support certain APIs.
 */
function collectClientMetadata(): LoginClientMetadata {
  const metadata: LoginClientMetadata = {};

  // Screen information
  if (typeof screen !== 'undefined') {
    metadata.screen_width = screen.width;
    metadata.screen_height = screen.height;
    metadata.screen_color_depth = screen.colorDepth;
    metadata.screen_pixel_ratio = window.devicePixelRatio;
  }

  // Viewport dimensions
  if (typeof window !== 'undefined') {
    metadata.viewport_width = window.innerWidth;
    metadata.viewport_height = window.innerHeight;
  }

  // Browser/environment info
  if (typeof navigator !== 'undefined') {
    metadata.language = navigator.language;
    metadata.languages = [...(navigator.languages || [])];
    metadata.platform = navigator.platform;
    metadata.vendor = navigator.vendor;
    metadata.cookie_enabled = navigator.cookieEnabled;
    metadata.do_not_track = navigator.doNotTrack;
    metadata.online = navigator.onLine;
    metadata.hardware_concurrency = navigator.hardwareConcurrency;
    metadata.max_touch_points = navigator.maxTouchPoints;

    // Device memory (Chrome only)
    const nav = navigator as Navigator & { deviceMemory?: number };
    if (nav.deviceMemory) {
      metadata.device_memory = nav.deviceMemory;
    }

    // Network Information API (Chrome only)
    const connection = (navigator as Navigator & {
      connection?: {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
    }).connection;
    if (connection) {
      metadata.connection_type = connection.type;
      metadata.connection_effective_type = connection.effectiveType;
      metadata.connection_downlink = connection.downlink;
      metadata.connection_rtt = connection.rtt;
      metadata.connection_save_data = connection.saveData;
    }
  }

  // Timezone information
  try {
    metadata.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    metadata.timezone_offset = new Date().getTimezoneOffset();
    metadata.client_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl not supported
  }

  // Page context
  if (typeof document !== 'undefined') {
    metadata.referrer = document.referrer || undefined;
    metadata.page_title = document.title || undefined;
  }
  if (typeof window !== 'undefined' && window.location) {
    metadata.page_url = window.location.href;
  }

  // Timestamp
  metadata.client_timestamp = new Date().toISOString();

  // Auth details
  metadata.auth_provider = 'github';
  metadata.auth_method = 'oauth';

  return metadata;
}

/**
 * Logs a login attempt to the backend.
 * Collects client-side metadata and sends it to the server which captures IP address.
 *
 * @param success - Whether the login attempt was successful
 * @param failureReason - Optional reason for failure (truncated to 80 chars)
 * @returns Promise resolving to the created login record
 */
export async function logLogin(success: boolean, failureReason?: string): Promise<Login> {
  const metadata = collectClientMetadata();
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  const payload: LogLoginRequest = {
    success,
    user_agent: userAgent,
    metadata,
  };

  if (!success && failureReason) {
    // Truncate failure reason to 80 characters as per schema constraint
    payload.failure_reason = failureReason.substring(0, 80);
  }

  return apiClient.post<Login, LogLoginRequest>('login/log', payload);
}
